import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { trackMemberInvited } from '@/utils/amplitudeEvents';

export interface InvitationData {
  id: string;
  email: string;
  company_id: string;
  invited_by: string | null;
  expires_at: string;
  used_at: string | null;
  created_at: string;
  role?: string;
}

export interface CreateInvitationData {
  email: string;
  role: string;
}

export const useInvitationManagement = () => {
  const [invitations, setInvitations] = useState<InvitationData[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { companyProfile } = useCompanyProfile();
  const { toast } = useToast();

  const fetchInvitations = useCallback(async () => {
    if (!companyProfile?.id) return [];

    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('company_invitations')
        .select('*')
        .eq('company_id', companyProfile.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setInvitations(data || []);
      return data || [];
    } catch (error) {
      console.error('Error fetching invitations:', error);
      toast({
        title: "Error",
        description: "Failed to fetch invitations",
        variant: "destructive"
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [companyProfile?.id, toast]);

  const createInvitation = useCallback(async (invitationData: CreateInvitationData) => {
    console.log('🚀 [INVITATION] Starting invitation creation process');

    if (!companyProfile?.id || !user?.id) {
      console.error('❌ [INVITATION] Missing required data - companyProfile or user');
      toast({
        title: "Error",
        description: "Company profile not loaded. Please try again.",
        variant: "destructive"
      });
      return false;
    }

    try {
      console.log('🔍 [INVITATION] Checking for existing invitation...');
      
      // Check if invitation already exists for this email
      const { data: existingInvitation, error: checkError } = await supabase
        .from('company_invitations')
        .select('id, used_at')
        .eq('company_id', companyProfile.id)
        .eq('email', invitationData.email)
        .is('used_at', null)
        .maybeSingle();

      if (checkError) {
        console.error('❌ [INVITATION] Error checking existing invitation:', checkError);
        throw checkError;
      }

      if (existingInvitation) {
        console.warn('⚠️ [INVITATION] Active invitation already exists');
        toast({
          title: "Error",
          description: "An active invitation already exists for this email address",
          variant: "destructive"
        });
        return false;
      }

      console.log('💾 [INVITATION] Creating invitation in database...');
      
      // Create the invitation in database first
      const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      
      const { data: invitation, error: dbError } = await supabase
        .from('company_invitations')
        .insert({
          email: invitationData.email,
          company_id: companyProfile.id,
          invited_by: user.id,
          expires_at: expiresAt,
          role: invitationData.role
        })
        .select()
        .single();

      if (dbError) {
        console.error('❌ [INVITATION] Database insertion error:', dbError);
        throw dbError;
      }

      console.log('✅ [INVITATION] Database invitation created successfully (id:', invitation.id, ')');

      // EVENT 5: Member Invited
      trackMemberInvited({
        company_id: companyProfile.id,
        company_name: companyProfile.name,
        role_invited: invitationData.role,
        invited_by: user.id,
        invited_email: invitationData.email,
      });

      // Prepare email data
      const emailData = {
        email: invitationData.email,
        companyName: companyProfile.name,
        companySlug: companyProfile.slug,
        inviterName: user.email?.split('@')[0] || 'Admin',
        inviterEmail: user.email,
        expiresAt,
        invitationId: invitation.id
      };

      console.log('🔗 [INVITATION] Calling Edge Function: send-invitation-email');

      try {
        // Send invitation email via Edge Function using Supabase Auth
        const { data: emailResult, error: emailError } = await supabase.functions.invoke(
          'send-invitation-email',
          {
            body: emailData
          }
        );

        if (emailError) {
          console.error('❌ [INVITATION] Edge Function call failed:', emailError);
          toast({
            title: "Invitation Created",
            description: `Invitation created for ${invitationData.email}. They can join using the registration link.`,
            variant: "destructive"
          });
        } else if (emailResult?.success) {
          console.log('✅ [INVITATION] Email sent successfully');
          toast({
            title: "Invitation Sent",
            description: `${invitationData.email} will receive an email with instructions to join.`,
          });
        } else {
          console.warn('⚠️ [INVITATION] Email result indicates failure or unexpected response');
          toast({
            title: "Invitation Created",
            description: `Invitation created for ${invitationData.email}. They can join using the registration link.`,
            variant: "destructive"
          });
        }

      } catch (edgeFunctionError) {
        console.error('💥 [INVITATION] Edge Function call threw exception:', edgeFunctionError);
        toast({
          title: "Invitation Created",
          description: `Invitation created for ${invitationData.email}. They can join using the registration link.`,
          variant: "destructive"
        });
      }

      // Refresh invitations list
      await fetchInvitations();
      
      console.log('✅ [INVITATION] Invitation creation process completed successfully');
      return true;
      
    } catch (error) {
      console.error('💥 [INVITATION] Critical error in invitation creation:', error?.code ?? error?.message ?? 'Unknown error');
      
      toast({
        title: "Error",
        description: `Failed to create invitation: ${error?.message || 'Unknown error'}. Please try again.`,
        variant: "destructive"
      });
      return false;
    }
  }, [companyProfile, user, toast, fetchInvitations]);

  const resendInvitation = useCallback(async (invitationId: string) => {
    try {
      // Get invitation details
      const { data: invitation, error: invitationError } = await supabase
        .from('company_invitations')
        .select('*')
        .eq('id', invitationId)
        .single();

      if (invitationError || !invitation) {
        throw new Error('Invitation not found');
      }

      // Update the invitation's expires_at to extend it by 7 days
      const newExpiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      
      const { error: updateError } = await supabase
        .from('company_invitations')
        .update({
          expires_at: newExpiresAt
        })
        .eq('id', invitationId);

      if (updateError) throw updateError;

      // Resend invitation email via Edge Function using Supabase Auth
      const { data: emailResult, error: emailError } = await supabase.functions.invoke(
        'send-invitation-email',
        {
          body: {
            email: invitation.email,
            companyName: companyProfile?.name || 'Your Company',
            companySlug: companyProfile?.slug || '',
            inviterName: user?.email?.split('@')[0] || 'Admin',
            inviterEmail: user?.email || '',
            expiresAt: newExpiresAt,
            invitationId: invitation.id
          }
        }
      );

      if (emailError || !emailResult?.success) {
        console.error('Error resending invitation email:', emailError);
        toast({
          title: "Warning",
          description: "Invitation was extended but Supabase Auth email may not have been sent. The user can still join using the registration link.",
          variant: "destructive"
        });
      }

      await fetchInvitations();
      return true;
    } catch (error) {
      console.error('Error resending invitation:', error);
      toast({
        title: "Error",
        description: "Failed to resend invitation",
        variant: "destructive"
      });
      return false;
    }
  }, [toast, fetchInvitations, companyProfile, user]);

  const revokeInvitation = useCallback(async (invitationId: string) => {
    try {
      const { error } = await supabase
        .from('company_invitations')
        .delete()
        .eq('id', invitationId);

      if (error) throw error;

      await fetchInvitations();
      return true;
    } catch (error) {
      console.error('Error revoking invitation:', error);
      toast({
        title: "Error",
        description: "Failed to revoke invitation",
        variant: "destructive"
      });
      return false;
    }
  }, [toast, fetchInvitations]);

  // New function to fix orphaned users
  const fixOrphanedUser = useCallback(async (email: string) => {
    console.log('🔧 [ORPHAN FIX] Attempting to fix orphaned user');
    
    try {
      // Call the fix-orphaned-user edge function
      const { data: result, error } = await supabase.functions.invoke(
        'fix-orphaned-user',
        { body: { email } }
      );

      if (error) throw error;

      if (result?.success) {
        // Refresh invitations to see updated status
        await fetchInvitations();
        return true;
      } else {
        throw new Error(result?.message || 'Failed to fix orphaned user');
      }
    } catch (error) {
      console.error('Error fixing orphaned user:', error);
      toast({
        title: "Error",
        description: `Failed to fix user ${email}: ${error.message}`,
        variant: "destructive"
      });
      return false;
    }
  }, [toast, fetchInvitations]);

  return {
    invitations,
    loading,
    fetchInvitations,
    createInvitation,
    resendInvitation,
    revokeInvitation,
    fixOrphanedUser
  };
};
